import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import MuiAppBar from "@mui/material/AppBar";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import { useAuth, useNotificationUnreadCount } from "@bw/shared/hooks";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Avatar } from "../../common/components/Avatar";
import { Crest } from "../../common/components/Crest";
import { QuickSearchField } from "../../common/components/QuickSearchField";
import { useMyProfile } from "../../common/query/useMyProfile";
import { useConversations } from "../../modules/social/hooks/useConversations";

export const PLAYER_APP_BAR_HEIGHT = "4rem";

function clampedBadge(count: number): number | string {
  return count > 99 ? "99+" : count;
}

function NotificationAction() {
  const unreadCount = useNotificationUnreadCount("player");
  const label =
    unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications";

  return (
    <Tooltip title="Notifications">
      <IconButton component={Link} to="/notifications" aria-label={label}>
        <Badge
          badgeContent={clampedBadge(unreadCount)}
          color="error"
          invisible={unreadCount <= 0}
          max={99}
        >
          <NotificationsRoundedIcon fontSize="small" aria-hidden="true" />
        </Badge>
      </IconButton>
    </Tooltip>
  );
}

function MessagesAction() {
  const conversations = useConversations().data ?? [];
  const unreadCount = conversations.reduce(
    (total, conversation) => total + conversation.unread_count,
    0,
  );
  const label =
    unreadCount > 0 ? `Messages, ${unreadCount} unread` : "Messages";

  return (
    <Tooltip title="Messages">
      <IconButton component={Link} to="/community/messages" aria-label={label}>
        <Badge
          badgeContent={clampedBadge(unreadCount)}
          color="error"
          invisible={unreadCount <= 0}
          max={99}
        >
          <ForumRoundedIcon fontSize="small" aria-hidden="true" />
        </Badge>
      </IconButton>
    </Tooltip>
  );
}

/** Booking is the primary action: one lime icon, the same at every width. */
function BookAction() {
  return (
    <Tooltip title="Book a court">
      <IconButton
        component={Link}
        to="/play?activity=court_reservation"
        aria-label="Book a court"
        sx={{
          bgcolor: "primary.main",
          color: "primary.contrastText",
          borderRadius: "10px",
          "&:hover": { bgcolor: "primary.dark" },
        }}
      >
        <EventAvailableRoundedIcon fontSize="small" aria-hidden="true" />
      </IconButton>
    </Tooltip>
  );
}

export function AppBar() {
  const { user } = useAuth();
  const signedIn = Boolean(user);
  const navigate = useNavigate();
  const { data: profile } = useMyProfile(signedIn);
  const displayName = profile?.display_name ?? null;
  const avatarUrl = profile?.avatar ?? null;
  const [searchOpen, setSearchOpen] = useState(false);

  return (
    <>
    <MuiAppBar position="fixed">
      <Toolbar
        disableGutters
        sx={{
          minHeight: `${PLAYER_APP_BAR_HEIGHT} !important`,
          px: { xs: 1.25, sm: 2.5 },
          gap: 1,
        }}
      >
        <Box
          component={Link}
          to="/"
          aria-label="Badminton Warriors home"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1.25,
            minWidth: 0,
            color: "text.primary",
            textDecoration: "none",
          }}
        >
          <Crest size="appBar" />
        </Box>

        <Box sx={{ flex: 1 }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {signedIn ? <BookAction /> : null}
          <Tooltip title="Search">
            <IconButton aria-label="Open search" onClick={() => setSearchOpen(true)}>
              <SearchRoundedIcon fontSize="small" aria-hidden="true" />
            </IconButton>
          </Tooltip>
          {signedIn ? (
            <>
              <NotificationAction />
              <MessagesAction />
              <Tooltip title="Your profile">
                <IconButton component={Link} to="/me" aria-label="Your profile">
                  <Avatar name={displayName} avatarUrl={avatarUrl} size={30} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Button variant="contained" onClick={() => navigate("/login")}>Sign in</Button>
          )}
        </Box>
      </Toolbar>
    </MuiAppBar>
    <Dialog
      open={searchOpen}
      onClose={() => setSearchOpen(false)}
      fullWidth
      maxWidth="sm"
      aria-labelledby="player-mobile-search-title"
    >
      <DialogTitle id="player-mobile-search-title">Search everything</DialogTitle>
      <Box sx={{ px: 2, pb: 2 }}>
        <QuickSearchField presentation="mobile" autoFocus onNavigate={() => setSearchOpen(false)} />
      </Box>
    </Dialog>
    </>
  );
}
